import React, { useState } from 'react';
import { Container, Row, Col, Card, Table, Button, Badge, Form, InputGroup, Modal } from 'react-bootstrap';
import { MdSearch, MdAdd, MdEdit, MdDelete, MdVisibility, MdEmail, MdPhone } from 'react-icons/md';

const Customers: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);

  const customers = [
    { id: 1, name: 'John Doe', email: 'john@example.com', phone: '+1-555-0123', status: 'Active', orders: 12, totalSpent: 1250.50 },
    { id: 2, name: 'Jane Smith', email: 'jane@example.com', phone: '+1-555-0124', status: 'Active', orders: 8, totalSpent: 890.25 },
    { id: 3, name: 'Bob Johnson', email: 'bob@example.com', phone: '+1-555-0125', status: 'Inactive', orders: 3, totalSpent: 299.99 },
    { id: 4, name: 'Alice Brown', email: 'alice@example.com', phone: '+1-555-0126', status: 'Active', orders: 15, totalSpent: 2100.75 },
    { id: 5, name: 'Charlie Wilson', email: 'charlie@example.com', phone: '+1-555-0127', status: 'Active', orders: 6, totalSpent: 450.00 }
  ];

  const getStatusBadge = (status: string) => {
    return status === 'Active' ? 
      <Badge bg="success">{status}</Badge> : 
      <Badge bg="secondary">{status}</Badge>;
  };

  const filteredCustomers = customers.filter(customer =>
    customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Container fluid className="py-4">
      <div className="page-header mb-4">
        <h2>Customer Database Management</h2>
        <p className="text-muted">Manage your customer database and relationships</p>
      </div>

      <Row className="mb-4">
        <Col>
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Customers List</h5>
              <Button variant="primary" onClick={() => setShowModal(true)}>
                <MdAdd className="me-1" />
                Add Customer
              </Button>
            </Card.Header>
            <Card.Body>
              <Row className="mb-3">
                <Col md={6}>
                  <InputGroup>
                    <InputGroup.Text>
                      <MdSearch />
                    </InputGroup.Text>
                    <Form.Control
                      type="text"
                      placeholder="Search customers..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </InputGroup>
                </Col>
              </Row>

              <Table responsive hover>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Status</th>
                    <th>Orders</th>
                    <th>Total Spent</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCustomers.map((customer) => (
                    <tr key={customer.id}>
                      <td>{customer.name}</td>
                      <td>
                        <div className="d-flex align-items-center">
                          <MdEmail className="me-1" />
                          {customer.email}
                        </div>
                      </td>
                      <td>
                        <div className="d-flex align-items-center">
                          <MdPhone className="me-1" />
                          {customer.phone}
                        </div>
                      </td>
                      <td>{getStatusBadge(customer.status)}</td>
                      <td>{customer.orders}</td>
                      <td>${customer.totalSpent.toFixed(2)}</td>
                      <td>
                        <Button variant="outline-primary" size="sm" className="me-1">
                          <MdVisibility />
                        </Button>
                        <Button variant="outline-secondary" size="sm" className="me-1">
                          <MdEdit />
                        </Button>
                        <Button variant="outline-danger" size="sm">
                          <MdDelete />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <h3 className="text-primary">892</h3>
              <p className="text-muted mb-0">Total Customers</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <h3 className="text-success">756</h3>
              <p className="text-muted mb-0">Active Customers</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <h3 className="text-warning">136</h3>
              <p className="text-muted mb-0">Inactive Customers</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <h3 className="text-info">$45,230</h3>
              <p className="text-muted mb-0">Total Revenue</p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Add Customer Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Add New Customer</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Full Name</Form.Label>
                  <Form.Control type="text" placeholder="Enter full name" />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Email</Form.Label>
                  <Form.Control type="email" placeholder="Enter email" />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Phone</Form.Label>
                  <Form.Control type="tel" placeholder="Enter phone number" />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Status</Form.Label>
                  <Form.Select>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Address</Form.Label>
              <Form.Control as="textarea" rows={3} placeholder="Enter address" />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => setShowModal(false)}>
            Add Customer
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default Customers;
